import React, { useState } from "react";
import LoadingWrapper from "../../../components/LoadingWrapper";
import { Button, Icon, InputNumber, message, Table, Tabs } from "antd";
import ReviewListLayout from "../components/ReviewListLayout";
import useRequest from "../../../utils/useRequest";
import {
    downStudentExamPaperList,
    ExamPaperItem,
    ExamType,
    getExamInfo,
    getExamMyCourseList,
    getStudentExamPaperList,
    OralScore,
    saveOralScore
} from "../../../service/exam";
import { errorMessage, toNumber } from "../../../service/service";
import usePromiseCallback from "../../../utils/usePromiseCallback";

const { TabPane } = Tabs;

interface PageLunWenReviewListProps {
    match: {
        params: {
            examId: string;
        };
    };
}

export default function PageOralReviewList({
    match: {
        params: { examId }
    }
}: PageLunWenReviewListProps) {
    const [examInfo, { loading: loadingExam }] = useRequest(() =>
        getExamInfo(examId)
    );
    const [list, { loading }] = useRequest(() => getExamMyCourseList(examId));

    return (
        <LoadingWrapper
            loading={loading || loadingExam}
            isEmpty={!list || !examInfo}>
            {() => (
                <ReviewListLayout examInfo={examInfo!}>
                    <Tabs type="card">
                        {list!.map(course => (
                            <TabPane
                                tab={course.courseName}
                                key={`${course.courseNo}${course.courseSeq}`}>
                                <ExamReviewItem
                                    courseNo={course.courseNo}
                                    courseSeq={course.courseSeq}
                                    examId={examId}
                                    channelId={examInfo!.examInfo}
                                />
                            </TabPane>
                        ))}
                    </Tabs>
                </ReviewListLayout>
            )}
        </LoadingWrapper>
    );
}

function ExamReviewItem({
    courseNo,
    courseSeq,
    examId,
    channelId
}: {
    courseNo: string;
    courseSeq: string;
    examId: string;
    channelId: string;
}) {
    const [examPaperList, { loading }] = useRequest(() =>
        getStudentExamPaperList({ courseNo, courseSeq, examId })
    );
    const [studentScores, setStudentScores] = useState<OralScore[]>([]);

    const columns = [
        {
            title: "姓名",
            dataIndex: "name"
        },
        {
            title: "学号",
            dataIndex: "studentNo"
        },
        {
            title: "成绩",
            dataIndex: "action",
            width: 100,
            render: (value: string, item: ExamPaperItem) => (
                <InputNumber
                    min={0}
                    max={100}
                    size="small"
                    defaultValue={toNumber(item.score)}
                    onChange={value => setScore(item.studentId, value || 0)}
                    precision={1}
                />
            )
        }
    ];

    // 设置分值
    function setScore(studentId: string, score: number) {
        const scores = [...studentScores];
        const index = scores.findIndex(item => item.studentId === studentId);

        if (index === -1) {
            setStudentScores([
                ...scores,
                { studentId, score, channelId, examId }
            ]);
        } else {
            scores[index].score = score;
            setStudentScores(scores);
        }
    }

    // 保存成绩
    const saveScore = usePromiseCallback(async function () {
        try {
            await saveOralScore(studentScores);
            message.info("保存成功");
        } catch (e) {
            message.error(errorMessage(e));
        }
    });

    const onExport = usePromiseCallback(async function () {
        downStudentExamPaperList({
            courseNo,
            courseSeq,
            examId,
            type: ExamType.Oral
        });
    });

    return (
        <Table
            loading={loading}
            rowKey={"studentId"}
            columns={columns}
            dataSource={examPaperList || []}
            bordered
            pagination={false}
            footer={currentPageData => (
                <TableFooter
                    data={currentPageData}
                    onSave={saveScore}
                    onExport={onExport}
                />
            )}
        />
    );
}

/**
 * 表尾
 */
function TableFooter({
    data,
    onExport,
    onSave
}: {
    data: any[];
    onSave: () => void;
    onExport: () => void;
}) {
    return (
        <div>
            <div style={{ textAlign: "right" }}>当前人数: {data.length}</div>
            <div style={{ textAlign: "center" }}>
                <Button
                    type="primary"
                    style={{ marginRight: 20 }}
                    onClick={onSave}>
                    <Icon type="cloud-upload" /> 保存
                </Button>
                <Button type="default" onClick={onExport}>
                    <Icon type="download" /> 导出
                </Button>
            </div>
        </div>
    );
}
